/**
 * Personal Numerology System
 * 
 * Comprehensive numerological calculations based on:
 * - Birth Date (Life Path, Personal Year/Month/Day)
 * - Birth Name (Expression, Soul Urge, Personality)
 * - Current Name (Current Expression)
 * - Destiny & Life Purpose
 * 
 * Based on Pythagorean and Chaldean systems
 * 
 * Sources:
 * - Pythagoras (c. 570-495 BCE) - Original numerology philosophy
 * - Modern numerology (Hans Decoz, Dan Millman, Glynis McCants)
 * - Chaldean system (ancient Babylonian)
 */

import { CORE_DIGIT_ARCHETYPES, getArchetype, getLayeredArchetypes } from './archetypes';

// ============================================================================
// NUMEROLOGY SYSTEMS
// ============================================================================

/**
 * Pythagorean Letter Values (Most Common System)
 * Based on alphabetical position, reduced to 1-9
 */
const PYTHAGOREAN_VALUES: { [key: string]: number } = {
  'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8, 'I': 9,
  'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'O': 6, 'P': 7, 'Q': 8, 'R': 9,
  'S': 1, 'T': 2, 'U': 3, 'V': 4, 'W': 5, 'X': 6, 'Y': 7, 'Z': 8
};

/**
 * Chaldean Letter Values (Ancient System)
 * Based on sound vibrations, no 9 (considered holy)
 */
const CHALDEAN_VALUES: { [key: string]: number } = {
  'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 8, 'G': 3, 'H': 5, 'I': 1,
  'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'O': 7, 'P': 8, 'Q': 1, 'R': 2,
  'S': 3, 'T': 4, 'U': 6, 'V': 6, 'W': 6, 'X': 5, 'Y': 1, 'Z': 7
};

const BASIC_VOWELS = ['A', 'E', 'I', 'O', 'U'];
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function parseBirthDateInput(birthDate: Date | string): Date {
  if (birthDate instanceof Date) {
    return new Date(birthDate.getTime());
  }

  if (DATE_ONLY_REGEX.test(birthDate)) {
    const [year, month, day] = birthDate.split('-').map(Number);
    // Use local noon to avoid timezone day-shift issues from UTC midnight parsing.
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }

  const parsed = new Date(birthDate);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`Invalid birth date: ${birthDate}`);
  }

  return parsed;
}

function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isVowelInName(letter: string, index: number, letters: string[]): boolean {
  if (BASIC_VOWELS.includes(letter)) return true;

  // Contextual Y rule: treat Y as vowel when not at start and not adjacent to A/E/I/O/U.
  if (letter !== 'Y' || index === 0) return false;

  const prev = letters[index - 1];
  const next = letters[index + 1];
  const prevIsVowel = !!prev && BASIC_VOWELS.includes(prev);
  const nextIsVowel = !!next && BASIC_VOWELS.includes(next);

  return !prevIsVowel && !nextIsVowel;
}

// ============================================================================
// CORE NUMEROLOGY FUNCTIONS
// ============================================================================

/**
 * Reduce number to single digit or master number
 * Master numbers (11, 22, 33, 44) are NOT reduced
 */
export function reduceNumber(num: number): number {
  // Preserve master numbers
  if (num === 11 || num === 22 || num === 33 || num === 44) return num;
  
  // Already single digit
  if (num < 10) return num;
  
  // Reduce by adding digits
  const digitSum = String(num)
    .split('')
    .reduce((sum, digit) => sum + parseInt(digit), 0);
  
  return reduceNumber(digitSum);
}

/**
 * Calculate sum of name using Pythagorean system
 */
export function calculateNameValue(
  name: string, 
  system: 'pythagorean' | 'chaldean' = 'pythagorean'
): number {
  const values = system === 'pythagorean' ? PYTHAGOREAN_VALUES : CHALDEAN_VALUES;
  const cleanName = name.toUpperCase().replace(/[^A-Z]/g, '');
  
  return cleanName
    .split('')
    .reduce((sum, char) => sum + (values[char] || 0), 0);
}

/**
 * Get number meaning with comprehensive interpretation,
 * integrating the full archetype library + layered digit archetype system.
 * For multi-digit numbers, explores all arrangements of combined archetypes.
 */
export function getNumberMeaning(number: number): {
  number: number;
  title: string;
  essence: string;
  layeredArchetypes?: string[];
  allArrangements?: Array<{ arrangement: string; terms: string[] }>;
  strengths: string[];
  challenges: string[];
  frequency: number;
  color: string;
  element: string;
} {
  // Use the comprehensive archetype library as the source of truth
  const archetype = getArchetype(number);

  // Get layered archetypes (explores all arrangements for multi-digit numbers)
  const layered = getLayeredArchetypes(number);
  
  // Primary essence uses the first (most direct) layered archetype
  const essence = layered.archetypes.length > 0 
    ? layered.archetypes[0]
    : archetype.essence;

  return {
    number: archetype.number,
    title: archetype.title,
    essence,
    layeredArchetypes: layered.archetypes.length > 1 ? layered.archetypes : undefined,
    allArrangements: layered.combinations.length > 1 ? layered.combinations : undefined,
    strengths: archetype.strengths,
    challenges: archetype.challenges,
    frequency: archetype.frequency,
    color: archetype.color,
    element: archetype.element
  };
}

// ============================================================================
// BIRTH DATE NUMEROLOGY
// ============================================================================

export interface LifePathCalculation {
  birthDate: string;
  dayValue: number;
  monthValue: number;
  yearValue: number;
  totalValue: number;
  lifePathNumber: number;
  meaning: ReturnType<typeof getNumberMeaning>;
  calculation: string;
}

/**
 * Calculate Life Path Number from birth date
 * This is the most important number in numerology - your life's purpose
 * 
 * Method: Add day + month + year, then reduce
 * Example: March 15, 1990
 *   Day: 15 = 1+5 = 6
 *   Month: 3 = 3
 *   Year: 1990 = 1+9+9+0 = 19 = 1+9 = 10 = 1+0 = 1
 *   Life Path: 6+3+1 = 10 = 1+0 = 1
 */
export function calculateLifePath(birthDate: Date | string): LifePathCalculation {
  const date = parseBirthDateInput(birthDate);
  
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  
  // Reduce each component separately (preserving master numbers)
  const dayValue = reduceNumber(day);
  const monthValue = reduceNumber(month);
  const yearValue = reduceNumber(
    String(year).split('').reduce((sum, digit) => sum + parseInt(digit), 0)
  );
  
  const totalValue = dayValue + monthValue + yearValue;
  const lifePathNumber = reduceNumber(totalValue);
  
  return {
    birthDate: formatDateLocal(date),
    dayValue,
    monthValue,
    yearValue,
    totalValue,
    lifePathNumber,
    meaning: getNumberMeaning(lifePathNumber),
    calculation: `Day(${day}→${dayValue}) + Month(${month}→${monthValue}) + Year(${year}→${yearValue}) = ${totalValue} → ${lifePathNumber}`
  };
}

/**
 * Calculate Birthday Number (just the day reduced)
 * Represents special gifts and talents
 */
export function calculateBirthdayNumber(birthDate: Date | string): number {
  const date = parseBirthDateInput(birthDate);
  return reduceNumber(date.getDate());
}

// ============================================================================
// PERSONAL CYCLES
// ============================================================================

export interface PersonalYear {
  year: number;
  personalYearNumber: number;
  meaning: ReturnType<typeof getNumberMeaning>;
  theme: string;
  guidance: string;
}

/**
 * Calculate Personal Year Number
 * Shows the energy and lessons for the current year
 * 
 * Formula: Birth day + Birth month + Current year (all reduced)
 * Example: Born March 15, calculating for 2024
 *   15 → 6, 3 → 3, 2024 → 8
 *   6 + 3 + 8 = 17 → 8 (Personal Year 8)
 */
export function calculatePersonalYear(
  birthDate: Date | string,
  targetYear?: number
): PersonalYear {
  const date = parseBirthDateInput(birthDate);
  const year = targetYear || new Date().getFullYear();
  
  const day = reduceNumber(date.getDate());
  const month = reduceNumber(date.getMonth() + 1);
  const yearValue = reduceNumber(
    String(year).split('').reduce((sum, digit) => sum + parseInt(digit), 0)
  );
  
  const personalYearNumber = reduceNumber(day + month + yearValue);
  const meaning = getNumberMeaning(personalYearNumber);
  
  const themes: { [key: number]: { theme: string; guidance: string } } = {
    1: { theme: 'New Beginnings', guidance: 'Plant seeds, start projects, be bold' },
    2: { theme: 'Cooperation & Patience', guidance: 'Build relationships, be patient, cooperate' },
    3: { theme: 'Creative Expression', guidance: 'Express yourself, socialize, create' },
    4: { theme: 'Building & Foundation', guidance: 'Work hard, organize, build foundations' },
    5: { theme: 'Change & Freedom', guidance: 'Embrace change, travel, be flexible' },
    6: { theme: 'Responsibility & Service', guidance: 'Take responsibility, nurture, serve others' },
    7: { theme: 'Spirituality & Reflection', guidance: 'Go within, study, develop spiritually' },
    8: { theme: 'Power & Abundance', guidance: 'Focus on career, finances, step into power' },
    9: { theme: 'Completion & Release', guidance: 'Let go, complete projects, prepare for new cycle' }
  };
  
  const yearTheme = themes[personalYearNumber] || themes[1];
  
  return {
    year,
    personalYearNumber,
    meaning,
    ...yearTheme
  };
}

/**
 * Calculate Personal Month Number
 * Shows energy for specific month within personal year
 * Formula: Personal Year + Current Month
 */
export function calculatePersonalMonth(
  birthDate: Date | string,
  targetMonth?: number,
  targetYear?: number
): number {
  const personalYear = calculatePersonalYear(birthDate, targetYear);
  const month = targetMonth || (new Date().getMonth() + 1);
  const monthValue = reduceNumber(month);
  
  return reduceNumber(personalYear.personalYearNumber + monthValue);
}

/**
 * Calculate Personal Day Number
 * Shows energy for specific day
 * Formula: Personal Month + Current Day
 */
export function calculatePersonalDay(
  birthDate: Date | string,
  targetDate?: Date
): number {
  const date = targetDate || new Date();
  const personalMonth = calculatePersonalMonth(
    birthDate,
    date.getMonth() + 1,
    date.getFullYear()
  );
  const dayValue = reduceNumber(date.getDate());
  
  return reduceNumber(personalMonth + dayValue);
}

// ============================================================================
// NAME NUMEROLOGY
// ============================================================================

export interface NameNumerology {
  name: string;
  expressionNumber: number; // Total of all letters (who you are)
  soulUrgeNumber: number;   // Vowels only (inner desires)
  personalityNumber: number; // Consonants only (outer persona)
  meanings: {
    expression: ReturnType<typeof getNumberMeaning>;
    soulUrge: ReturnType<typeof getNumberMeaning>;
    personality: ReturnType<typeof getNumberMeaning>;
  };
  letterBreakdown: Array<{
    letter: string;
    value: number;
    type: 'vowel' | 'consonant';
  }>;
}

/**
 * Calculate complete name numerology
 * 
 * - Expression Number: Sum of ALL letters (who you truly are)
 * - Soul Urge Number: Sum of VOWELS (inner motivations)
 * - Personality Number: Sum of CONSONANTS (how others see you)
 */
export function calculateNameNumerology(
  fullName: string,
  system: 'pythagorean' | 'chaldean' = 'pythagorean'
): NameNumerology {
  const values = system === 'pythagorean' ? PYTHAGOREAN_VALUES : CHALDEAN_VALUES;
  const cleanName = fullName.toUpperCase().replace(/[^A-Z]/g, '');
  const letters = cleanName.split('');
  
  let totalSum = 0;
  let vowelSum = 0;
  let consonantSum = 0;
  
  const letterBreakdown = letters.map((letter, index) => {
    const value = values[letter] || 0;
    const isVowel = isVowelInName(letter, index, letters);
    
    totalSum += value;
    if (isVowel) vowelSum += value;
    else consonantSum += value;
    
    return {
      letter,
      value,
      type: isVowel ? 'vowel' as const : 'consonant' as const
    };
  });
  
  return {
    name: fullName,
    expressionNumber: reduceNumber(totalSum),
    soulUrgeNumber: reduceNumber(vowelSum),
    personalityNumber: reduceNumber(consonantSum),
    meanings: {
      expression: getNumberMeaning(reduceNumber(totalSum)),
      soulUrge: getNumberMeaning(reduceNumber(vowelSum)),
      personality: getNumberMeaning(reduceNumber(consonantSum))
    },
    letterBreakdown
  };
}

export interface CoreNumerologySnapshot {
  lifePathNumber: number;
  birthdayNumber: number;
  expressionNumber: number;
  soulUrgeNumber: number;
  personalityNumber: number;
  maturityNumber: number;
}

function buildCoreNumerologySnapshot(
  lifePathNumber: number,
  birthdayNumber: number,
  nameProfile: NameNumerology
): CoreNumerologySnapshot {
  return {
    lifePathNumber,
    birthdayNumber,
    expressionNumber: nameProfile.expressionNumber,
    soulUrgeNumber: nameProfile.soulUrgeNumber,
    personalityNumber: nameProfile.personalityNumber,
    maturityNumber: reduceNumber(lifePathNumber + nameProfile.expressionNumber)
  };
}

// ============================================================================
// COMPLETE PERSONAL NUMEROLOGY PROFILE
// ============================================================================

export interface CompleteNumerologyProfile {
  birthDate: string;
  birthName: string;
  currentName?: string;
  activeProfileName: string;
  
  // Core Numbers
  lifePath: LifePathCalculation;
  birthday: number;
  birthdayMeaning: ReturnType<typeof getNumberMeaning>;
  
  // Name Numbers
  birthNameProfile: NameNumerology;
  currentNameProfile?: NameNumerology;
  activeNameProfile: NameNumerology;

  // Core snapshots (birth certificate name vs active profile name)
  birthCoreNumbers: CoreNumerologySnapshot;
  activeCoreNumbers: CoreNumerologySnapshot;
  
  // Current Cycles
  currentYear: PersonalYear;
  currentMonth: number;
  currentDay: number;
  
  // Personal Frequencies
  primaryFrequency: number; // Based on Life Path
  secondaryFrequency: number; // Based on Expression
  experimentalFrequency: number; // Based on current year
  
  // Summary
  summary: {
    coreIdentity: string;
    currentPhase: string;
    mainChallenges: string[];
    keyStrengths: string[];
  };
}

/**
 * Generate complete numerology profile for an individual
 */
export function generateCompleteProfile(
  birthDate: Date | string,
  birthName: string,
  currentName?: string
): CompleteNumerologyProfile {
  // Core calculations
  const lifePath = calculateLifePath(birthDate);
  const birthday = calculateBirthdayNumber(birthDate);
  const birthdayMeaning = getNumberMeaning(birthday);
  
  // Name calculations
  const birthNameProfile = calculateNameNumerology(birthName);
  const normalizedCurrentName = currentName?.trim();
  const currentNameProfile = normalizedCurrentName
    ? calculateNameNumerology(normalizedCurrentName)
    : undefined;
  const activeProfileName = normalizedCurrentName && normalizedCurrentName.length > 0
    ? normalizedCurrentName
    : birthName;
  const activeNameProfile = currentNameProfile || birthNameProfile;
  
  // Current cycles
  const currentYear = calculatePersonalYear(birthDate);
  const currentMonth = calculatePersonalMonth(birthDate);
  const currentDay = calculatePersonalDay(birthDate);
  
  // Personal frequencies
  const primaryFrequency = lifePath.meaning.frequency;
  const secondaryFrequency = activeNameProfile.meanings.expression.frequency;
  const experimentalFrequency = currentYear.meaning.frequency;

  const birthCoreNumbers = buildCoreNumerologySnapshot(
    lifePath.lifePathNumber,
    birthday,
    birthNameProfile
  );
  const activeCoreNumbers = buildCoreNumerologySnapshot(
    lifePath.lifePathNumber,
    birthday,
    activeNameProfile
  );
  
  // Generate summary
  const coreIdentity = `Life Path ${lifePath.lifePathNumber} (${lifePath.meaning.title}) with ${activeProfileName} Expression ${activeNameProfile.expressionNumber} (${activeNameProfile.meanings.expression.title})`;
  const currentPhase = `Personal Year ${currentYear.personalYearNumber} - ${currentYear.theme}`;
  
  const mainChallenges = [
    ...lifePath.meaning.challenges,
    ...activeNameProfile.meanings.expression.challenges
  ].slice(0, 3);
  
  const keyStrengths = [
    ...lifePath.meaning.strengths,
    ...activeNameProfile.meanings.expression.strengths
  ].slice(0, 5);
  
  return {
    birthDate: formatDateLocal(parseBirthDateInput(birthDate)),
    birthName,
    currentName,
    activeProfileName,
    lifePath,
    birthday,
    birthdayMeaning,
    birthNameProfile,
    currentNameProfile,
    activeNameProfile,
    birthCoreNumbers,
    activeCoreNumbers,
    currentYear,
    currentMonth,
    currentDay,
    primaryFrequency,
    secondaryFrequency,
    experimentalFrequency,
    summary: {
      coreIdentity,
      currentPhase,
      mainChallenges,
      keyStrengths
    }
  };
}

// ============================================================================
// COMPATIBILITY CALCULATIONS
// ============================================================================

export interface CompatibilityScore {
  person1LifePath: number;
  person2LifePath: number;
  compatibilityScore: number; // 0-100
  harmonicResonance: 'Challenging' | 'Compatible' | 'Harmonious' | 'Soul Connection';
  strengths: string[];
  challenges: string[];
  advice: string;
}

/**
 * Calculate numerological compatibility between two people
 */
export function calculateCompatibility(
  person1BirthDate: Date | string,
  person2BirthDate: Date | string
): CompatibilityScore {
  const lifePath1 = calculateLifePath(person1BirthDate).lifePathNumber;
  const lifePath2 = calculateLifePath(person2BirthDate).lifePathNumber;
  
  // Compatibility matrix (simplified - based on traditional numerology)
  const compatibilityMatrix: { [key: string]: number } = {
    '1-1': 60, '1-2': 85, '1-3': 75, '1-4': 50, '1-5': 90, '1-6': 70, '1-7': 65, '1-8': 80, '1-9': 75,
    '2-2': 80, '2-3': 85, '2-4': 90, '2-5': 55, '2-6': 95, '2-7': 85, '2-8': 75, '2-9': 90,
    '3-3': 70, '3-4': 60, '3-5': 95, '3-6': 85, '3-7': 60, '3-8': 70, '3-9': 88,
    '4-4': 85, '4-5': 50, '4-6': 90, '4-7': 80, '4-8': 95, '4-9': 65,
    '5-5': 75, '5-6': 60, '5-7': 85, '5-8': 80, '5-9': 88,
    '6-6': 90, '6-7': 75, '6-8': 80, '6-9': 95,
    '7-7': 80, '7-8': 70, '7-9': 85,
    '8-8': 75, '8-9': 80,
    '9-9': 90
  };
  
  const key = `${Math.min(lifePath1, lifePath2)}-${Math.max(lifePath1, lifePath2)}`;
  const score = compatibilityMatrix[key] || 70;
  
  let harmonicResonance: CompatibilityScore['harmonicResonance'];
  if (score >= 90) harmonicResonance = 'Soul Connection';
  else if (score >= 75) harmonicResonance = 'Harmonious';
  else if (score >= 60) harmonicResonance = 'Compatible';
  else harmonicResonance = 'Challenging';
  
  const strengths = [
    'Complementary energies',
    'Mutual growth potential',
    'Balanced dynamic'
  ];
  
  const challenges = [
    'Different approaches to life',
    'Communication gaps',
    'Need for understanding'
  ];
  
  const advice = score >= 75 
    ? 'This is a naturally harmonious pairing. Focus on nurturing what works.'
    : 'This relationship requires conscious effort and understanding. Growth through challenge.';
  
  return {
    person1LifePath: lifePath1,
    person2LifePath: lifePath2,
    compatibilityScore: score,
    harmonicResonance,
    strengths,
    challenges,
    advice
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Core functions
  reduceNumber,
  calculateNameValue,
  getNumberMeaning,
  
  // Birth date
  calculateLifePath,
  calculateBirthdayNumber,
  
  // Cycles
  calculatePersonalYear,
  calculatePersonalMonth,
  calculatePersonalDay,
  
  // Name
  calculateNameNumerology,
  
  // Complete profile
  generateCompleteProfile,
  
  // Compatibility
  calculateCompatibility
};
